import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Palette, Sparkles, MapPin, ShieldCheck, Heart, Users, Star, Instagram, CalendarDays, MessageCircleHeart, PartyPopper, Cake, Tent, School, Building2, Quote } from "lucide-react";
import { api, brand } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { WaveTop, WaveBottom, Blob } from "@/components/Deco";
import BookCta from "@/components/BookCta";

const trust = [
  { icon: MapPin, label: "Nanaimo Based" },
  { icon: ShieldCheck, label: "Licensed & Insured" },
  { icon: Heart, label: "Patient & Fun" },
  { icon: Users, label: "Parties & Events of All Ages" },
];

const featured = [
  { icon: Cake, color: "#F43BA5", title: "Birthday Parties", text: "Themed designs and favourite characters for birthdays of all ages." },
  { icon: Tent, color: "#28BCEB", title: "Markets & Festivals", text: "Reliable, high-volume face painting for busy community events." },
  { icon: School, color: "#65A95A", title: "Schools & Community", text: "Licensed, insured fun for school nights and fundraisers." },
  { icon: Building2, color: "#8754D9", title: "Corporate & Weddings", text: "Elegant, professional painting for grown-up celebrations." },
];

const steps = [
  { icon: PartyPopper, title: "Pick Your Event", text: "Choose the type of event and how you’d like to book." },
  { icon: CalendarDays, title: "Choose an Available Time", text: "See real openings and grab the slot that suits you." },
  { icon: MessageCircleHeart, title: "Tell Tay About Your Party", text: "Share your theme, location and a few fun details." },
  { icon: ShieldCheck, title: "Receive Your Confirmation", text: "Tay reviews your request and confirms your booking." },
];

const why = [
  { icon: ShieldCheck, title: "Licensed & insured", text: "Book with total confidence — perfect for schools and venues." },
  { icon: Heart, title: "Endlessly patient", text: "Gentle and warm with even the shyest little painters." },
  { icon: Sparkles, title: "Beautiful designs", text: "Professional-quality art that wows kids and grown-ups alike." },
  { icon: MapPin, title: "Local to Nanaimo", text: "A friendly island artist who knows the community." },
];

export default function Home() {
  useSeo({ title: "Nanaimo Face Painter — Birthdays, Markets & Events", description: "Paint With Tay is a licensed & insured Nanaimo face painter for birthday parties, markets, schools, festivals and special events. Book online today!" });
  const [content, setContent] = useState({});
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data || {})).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data || [])).catch(() => {});
    api.get("/gallery").then((r) => setGallery((r.data || []).slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-cream">
        <Blob className="absolute -top-16 -left-16 w-72 h-72 opacity-30 animate-float" color="#FFD447" />
        <Blob className="absolute top-40 -right-20 w-80 h-80 opacity-25 animate-float" color="#28BCEB" />
        <div className="section pt-16 pb-8 relative text-center max-w-4xl mx-auto">
          <Reveal>
            <span className="chip bg-sunshine"><Sparkles className="w-4 h-4" /> Nanaimo & surrounding communities</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-700 leading-[1.05] mt-4">
              Big Smiles. <span className="text-hotpink">Bright Colours.</span> Unforgettable Faces.
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-ink/80 max-w-2xl mx-auto">
              {content.hero_subtext || "Fun, professional face painting for birthdays, markets, schools and special events throughout Nanaimo and surrounding communities."}
            </p>
            <div className="mt-7 flex flex-wrap gap-4 justify-center">
              <Link to="/book" className="btn-pink text-lg" data-testid="hero-book-btn"><Palette className="w-5 h-5" /> Book Paint With Tay</Link>
              <Link to="/gallery" className="btn-blue text-lg" data-testid="hero-gallery-btn"><Sparkles className="w-5 h-5" /> See the Magic</Link>
            </div>
          </Reveal>
        </div>

        {/* TRUST BAR */}
        <div className="section pb-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {trust.map((t) => (
              <div key={t.label} className="card-soft py-4 px-3 flex items-center gap-3 justify-center text-center" data-testid={`trust-${t.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <t.icon className="w-6 h-6 text-hotpink shrink-0" />
                <span className="font-heading font-500 text-sm sm:text-base">{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <WaveTop color="#8754D9" />
      <section className="bg-purple text-white">
        <div className="section py-14 grid lg:grid-cols-2 gap-8 items-center">
          <Reveal>
            <p className="font-accent text-3xl text-sunshine">Happy faces are kind of Tay’s thing.</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-700 mt-1 mb-4">Meet Paint With Tay</h2>
            <p className="text-white/90 text-lg">Hi, I’m Tayler! A Nanaimo-based artist who loves colour, creativity and community. You’ll find me in my grey overalls and comfy boots, palette in hand, turning tiny tigers into sparkling butterflies — and everything in between. Whether it’s a backyard birthday or a bustling market, let’s add a little colour to your celebration.</p>
            <Link to="/about" className="btn-yellow mt-6" data-testid="intro-about-btn">More about Tay</Link>
          </Reveal>
          <Reveal delay={0.1} className="grid grid-cols-2 gap-4">
            {featured.map((f) => (
              <div key={f.title} className="bg-white text-ink rounded-[1.5rem] border-[3px] border-ink p-5" style={{ boxShadow: "5px 5px 0 0 #292634" }}>
                <div className="w-11 h-11 rounded-xl border-[3px] border-ink flex items-center justify-center mb-2" style={{ backgroundColor: f.color }}><f.icon className="w-6 h-6 text-white" /></div>
                <h3 className="font-heading text-lg font-600">{f.title}</h3>
                <p className="text-sm text-ink/75">{f.text}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>
      <WaveBottom color="#8754D9" />

      {/* GALLERY PREVIEW */}
      <section className="section py-12">
        <Reveal>
          <div className="text-center mb-8">
            <p className="font-accent text-3xl text-hotpink">A splash of colour</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-700">Recent faces & favourites</h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {gallery.map((g, i) => (
            <Reveal key={g.id} delay={(i % 3) * 0.06}>
              <div className="card-soft overflow-hidden p-0" style={{ transform: `rotate(${(i % 3) - 1}deg)` }}>
                <img src={g.url} alt={g.alt || g.caption} loading="lazy" className="w-full h-44 sm:h-52 object-cover" />
              </div>
            </Reveal>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/gallery" className="btn-blue" data-testid="home-gallery-btn"><Sparkles className="w-5 h-5" /> View the full gallery</Link>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <WaveTop color="#28BCEB" />
      <section className="bg-brightblue">
        <div className="section py-14">
          <Reveal>
            <div className="text-center mb-10">
              <p className="font-accent text-3xl text-white">Easy as can be</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-700">How booking works</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="card-pop p-6 h-full text-center relative">
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-sunshine border-[3px] border-ink flex items-center justify-center font-heading font-700">{i + 1}</span>
                  <div className="w-14 h-14 mx-auto rounded-full bg-hotpink border-[3px] border-ink flex items-center justify-center mt-3 mb-3"><s.icon className="w-7 h-7 text-white" /></div>
                  <h3 className="font-heading text-lg font-600 mb-1">{s.title}</h3>
                  <p className="text-ink/75 text-sm">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <WaveBottom color="#28BCEB" />

      {/* WHY CHOOSE */}
      <section className="section py-12">
        <Reveal>
          <div className="text-center mb-8">
            <p className="font-accent text-3xl text-hotpink">Trusted by families & organizers</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-700">Why choose Tay</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {why.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.07}>
              <div className="card-soft p-6 h-full">
                <div className="w-12 h-12 rounded-full bg-leaf border-[3px] border-ink flex items-center justify-center mb-3"><w.icon className="w-6 h-6 text-white" /></div>
                <h3 className="font-heading text-lg font-600 mb-1">{w.title}</h3>
                <p className="text-ink/75 text-sm">{w.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section pb-14">
        <Reveal>
          <div className="text-center mb-8">
            <p className="font-accent text-3xl text-hotpink">Kind words</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-700">What people say</h2>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <Reveal key={t.id} delay={(i % 2) * 0.08}>
              <div className="card-soft p-6 h-full">
                <Quote className="w-8 h-8 text-sunshine mb-2" />
                <p className="text-ink/85 text-lg italic mb-4">“{t.quote}”</p>
                <div className="flex items-center gap-1 mb-1">
                  {Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-sunshine text-sunshine" />)}
                </div>
                <p className="font-heading font-600">{t.name}</p>
                <p className="text-ink/60 text-sm">{t.role}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INSTAGRAM + SERVICE AREA */}
      <section className="section pb-14 grid md:grid-cols-2 gap-5">
        <Reveal>
          <a href={brand.instagram} target="_blank" rel="noopener noreferrer" className="card-soft p-8 flex items-center gap-5 h-full" data-testid="home-instagram">
            <div className="w-16 h-16 rounded-2xl bg-hotpink border-[3px] border-ink flex items-center justify-center shrink-0"><Instagram className="w-8 h-8 text-white" /></div>
            <div>
              <h3 className="font-heading text-2xl font-700">Follow the fun</h3>
              <p className="text-ink/75">See the latest designs on Instagram <span className="text-hotpink font-700">@paint.with.tay</span></p>
            </div>
          </a>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="card-soft p-8 flex items-center gap-5 h-full">
            <div className="w-16 h-16 rounded-2xl bg-leaf border-[3px] border-ink flex items-center justify-center shrink-0"><MapPin className="w-8 h-8 text-white" /></div>
            <div>
              <h3 className="font-heading text-2xl font-700">Service area</h3>
              <p className="text-ink/75">Proudly serving <span className="font-700">{content.service_area || "Nanaimo and surrounding communities"}</span> on Vancouver Island.</p>
            </div>
          </div>
        </Reveal>
      </section>

      <BookCta />
    </div>
  );
}
